import { HIGHER_RISK_ACTIVITIES } from '../intake.js';
/**
 * Underwriting facts (task 88): what insurers' proposal forms ask that the control library doesn't — the
 * company's registration details, revenue split, records held, account counts, patch targets, critical
 * systems, losses and current cover. Keyed `UW-…`. They are published with the library so insurer forms can
 * read them; task 89 places them in the journey's stages.
 */
const yesNo = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
const options = (o) => Object.entries(o).map(([value, label]) => ({ value, label }));
const bool = (text, help = '') => ({ type: 'boolean', text, help, options: yesNo }); // prettier-ignore
const pct = (text, help = '') => ({ type: 'percentage', text, help });
const count = (text, help = '') => ({ type: 'numeric', text, help, settings: { integer: true, min: 0 } }); // prettier-ignore
const text = (t, help = '', multiline = false) => ({ type: 'free_text', text: t, help, settings: { max_length: multiline ? 3000 : 300, multiline } }); // prettier-ignore
const pick = (t, o, help = '') => ({ type: 'single_choice', text: t, help, options: options(o) }); // prettier-ignore
const many = (t, o, help = '') => ({ type: 'multiple_choice', text: t, help, options: options(o) }); // prettier-ignore
const money = (t, help = '') => ({ type: 'currency', text: t, help, settings: { currency: 'MYR', max: 100_000_000_000 } }); // prettier-ignore
const when = (q, value = 'yes', op = 'eq') => ({ visibility: { question: q, op, value }, depends_on: [q] }); // prettier-ignore
const SCAN_CADENCE = { continuous: 'Continuously', monthly: 'Every month', quarterly: 'Every quarter', yearly: 'Once a year', never: 'Never' }; // prettier-ignore
export const UNDERWRITING_FACTS = {
    // ---------- the company ----------
    'UW-REG-NO': text('What is the company’s registration number (SSM)?'),
    'UW-TAX-NO': text('What is the company’s tax identification number (TIN)?'),
    'UW-SST-NO': { ...text('What is the company’s SST registration number, if it has one?'), mandatory: false },
    'UW-ADDRESS': { type: 'location', text: 'What is the company’s principal address?', settings: { granularity: 'address' } }, // prettier-ignore
    'UW-YEAR-EST': { type: 'numeric', text: 'In which year was the business established?', settings: { integer: true, min: 1800, max: 2100 } }, // prettier-ignore
    'UW-SEC-CONTACT': {
        type: 'repeating_group',
        text: 'Who is responsible for data and network security?',
        help: 'Your CISO, IT manager or the person who looks after security.',
        settings: {
            item_label: 'Person',
            max_items: 1,
            fields: [
                { key: 'name', label: 'Name', type: 'text', required: true },
                { key: 'role', label: 'Role', type: 'text', required: true },
                { key: 'email', label: 'Email', type: 'text', required: true },
                { key: 'phone', label: 'Phone', type: 'text' },
            ],
        },
    },
    'UW-ACTIVITIES': text('What does the business do to earn its revenue, including its subsidiaries?', '', true),
    'UW-PARENT': bool('Is the business a subsidiary or franchisee of a larger organisation?'),
    'UW-PARENT-DETAIL': {
        ...text('Which organisation, and how is the business related to it?'),
        ...when('UW-PARENT'),
    },
    'UW-SUBSIDIARIES': bool('Does the business have subsidiaries of its own?'),
    'UW-GROUP-SECURITY': {
        ...many('How is cyber security run across the subsidiaries?', {
            central_management: 'Managed centrally for every subsidiary',
            central_baseline: 'A central baseline every subsidiary must meet',
            audited: 'Sites and subsidiaries are audited against it',
            none: 'None of these',
        }),
        ...when('UW-SUBSIDIARIES'),
    },
    'UW-SPECIAL-ACTIVITIES': many('Does the business do any of these?', HIGHER_RISK_ACTIVITIES, 'Insurers treat these activities as higher risk.'), // prettier-ignore
    'UW-SANCTIONS': bool('Does the business trade with anyone in a sanctioned country, such as Iran, Syria, North Korea, Cuba, Venezuela or Crimea?'), // prettier-ignore
    'UW-OVERSEAS-ENTITIES': bool('Does the business have offices or subsidiaries outside Malaysia that need cover?'),
    'UW-OVERSEAS-LIST': {
        type: 'table',
        text: 'Which offices or subsidiaries outside Malaysia, and what share of revenue does each earn?',
        ...when('UW-OVERSEAS-ENTITIES'),
        settings: {
            min_rows: 1,
            columns: [
                { key: 'entity', label: 'Name', type: 'text', required: true },
                {
                    key: 'country',
                    label: 'Country (and state, for the USA or Australia)',
                    type: 'text',
                    required: true,
                },
                { key: 'revenue_pct', label: '% of revenue', type: 'number' },
            ],
        },
    },
    // ---------- revenue ----------
    'UW-REVENUE-CURRENT': money('What revenue do you expect this financial year?', 'Including fee income.'),
    'UW-REVENUE-NEXT': money('What revenue do you project for next financial year?'),
    'UW-REVENUE-OVERSEAS-PCT': pct('What share of revenue comes from customers outside Malaysia?'),
    'UW-REVENUE-US-PCT': pct('What share of revenue comes from the USA and Canada?'),
    'UW-ONLINE-PCT': pct('What share of revenue comes from online sales?'),
    // ---------- data held ----------
    'UW-PII-RECORDS': count('Roughly how many people and organisations would you have to notify if their personal data leaked?', 'Customers, staff and others, counted once each.'), // prettier-ignore
    'UW-CARD-RECORDS': count('For how many people do you hold payment card or bank account details?'),
    'UW-HEALTH-RECORDS': count('For how many people do you hold health records?'),
    'UW-PROCESSOR': bool('Do you process personal data on behalf of other businesses?'),
    'UW-PROCESSOR-DETAIL': { ...text('Whose data, and what do you do with it?'), ...when('UW-PROCESSOR') },
    'UW-PCI': bool('Does the business take or process card payments?'),
    'UW-PCI-TXNS': { ...count('Roughly how many card transactions do you process a year?'), ...when('UW-PCI') },
    'UW-PCI-OUTSOURCED': {
        ...bool('Does a payment provider handle your PCI DSS duties for you?'),
        ...when('UW-PCI'),
    },
    'UW-PCI-LEVEL': {
        ...pick('What is your (or your provider’s) PCI DSS compliance level?', { level_1: 'Level 1', level_2: 'Level 2', level_3: 'Level 3', level_4: 'Level 4', not_compliant: 'Not compliant' }), // prettier-ignore
        ...when('UW-PCI'),
    },
    'UW-PDPA-REG': pick('Is the business registered with the Personal Data Protection Commissioner?', { registered: 'Yes, registered', not_required: 'Not required for our class of business', no: 'No' }, 'Businesses in the classes the PDPA lists (banking, insurance, retail, healthcare, education and others) must register.'),
    'UW-PDPA-REG-DATE': { type: 'date', text: 'When was the certificate of registration issued?', settings: { not_in_future: true }, ...when('UW-PDPA-REG', 'registered') }, // prettier-ignore
    'UW-COMPLIANCE-REVIEW': pick('In the last 12 months, have you checked that you comply with the data protection and security laws that apply to you?', { yes: 'Yes', partly: 'Partly', no: 'No' }), // prettier-ignore
    'UW-STANDARDS': many('Which security standards does the business follow or hold?', {
        iso_27001: 'ISO/IEC 27001',
        nist_csf: 'NIST Cybersecurity Framework',
        cis: 'CIS Controls',
        pci_dss: 'PCI DSS',
        soc2: 'SOC 2',
        bnm_rmit: 'BNM RMiT',
        other: 'Another standard',
        none: 'None',
    }),
    'UW-PII-PROTECTIONS': many('Which of these protect the personal data you hold?', {
        need_to_know: 'Access limited to those who need it',
        microsegmentation: 'Databases with sensitive data isolated on their own network segment',
        anonymisation: 'Anonymisation',
        pseudonymisation: 'Pseudonymisation',
        tokenisation: 'Tokenisation',
        db_encryption: 'Encrypted at rest at database level',
        field_encryption: 'Encrypted at rest at field level',
        transit_encryption: 'Encrypted in transit',
        dlp: 'Data loss prevention tools',
        none: 'None of these',
    }),
    'UW-PII-ENCRYPTED-PCT': pct('What share of the personal data you hold is encrypted at rest?'),
    'UW-PII-LARGEST-STORE': count('What is the largest number of people whose personal data sits in a single database or system?'), // prettier-ignore
    'UW-PII-PROCESSORS': pick('Do outside providers process personal data for you?', { yes: 'Yes', partly: 'Some of it', no: 'No' }), // prettier-ignore
    'UW-PROCESSOR-CONTRACTS': {
        ...many('What do your contracts with those providers cover?', {
            written_contracts: 'A written contract with every provider',
            privacy_reviewed: 'Reviewed for compliance with privacy law',
            breach_responsibility: 'Who responds to a data breach',
            none: 'None of these',
        }),
        ...when('UW-PII-PROCESSORS', 'no', 'ne'),
    },
    'UW-RECOURSE-WAIVED': pick('How often do you give up your right to claim against technology providers if their service fails?', { never: 'Never or rarely', sometimes: 'Sometimes', mostly: 'Always or most of the time' }), // prettier-ignore
    'UW-BIOMETRIC': bool('Do you collect or store biometric data, such as fingerprints, face scans or fingerprint time clocks?'), // prettier-ignore
    'UW-BIOMETRIC-FROM': { ...many('Whose biometric data?', { employees: 'Employees', contractors: 'Service providers or contractors', customers: 'Customers' }), ...when('UW-BIOMETRIC') }, // prettier-ignore
    'UW-BIOMETRIC-CONSENT': { ...bool('Do you always get explicit written consent before collecting it?'), ...when('UW-BIOMETRIC') }, // prettier-ignore
    'UW-BIOMETRIC-POLICY': { ...bool('Is there a written policy on how long biometric data is kept and how it is destroyed?'), ...when('UW-BIOMETRIC') }, // prettier-ignore
    'UW-BIOMETRIC-RECORDS': {
        ...count('For how many people do you hold biometric data?'),
        ...when('UW-BIOMETRIC'),
    },
    // ---------- network and access ----------
    'UW-HOSTED-CENTRALLY': pick('Are your critical systems and applications hosted centrally?', { yes: 'Yes', partly: 'Partly', no: 'No' }), // prettier-ignore
    'UW-SEGMENTATION': many('How is your network divided to stop malware or an intruder moving between systems?', {
        vlan: 'VLANs',
        host_firewalls: 'Host-based firewalls',
        acl: 'Firewall rules (access control lists)',
        air_gap: 'Air gaps',
        sdn: 'Software-defined networking',
        least_privilege: 'Least-privilege access',
        none: 'It isn’t divided (a flat network)',
    }),
    'UW-PENTEST': many('Which penetration tests are done at least once a year?', { external: 'Of important internet-facing systems', internal: 'Of important internal systems', none: 'Neither' }), // prettier-ignore
    'UW-MOBILE-ACCESS': bool('Can laptops, tablets or phones reach company applications and data?'),
    'UW-MDM-COMPANY-PCT': { ...pct('What share of company laptops, tablets and phones are managed by device management?'), ...when('UW-MOBILE-ACCESS') }, // prettier-ignore
    'UW-MDM-BYOD-PCT': { ...pct('What share of personal devices (BYOD) used for work are managed?', 'Leave blank if staff don’t use their own devices.'), ...when('UW-MOBILE-ACCESS'), mandatory: false }, // prettier-ignore
    'UW-REMOTE-METHODS': {
        ...many('How is remote access to the company network secured?', { vpn: 'VPN', mfa: 'Multi-factor authentication', sso_mfa: 'Single sign-on with MFA', ztna: 'Zero trust network access', encryption: 'Encrypted traffic', other: 'Something else' }), // prettier-ignore
        ...when('P-REMOTE'),
    },
    'UW-REMOTE-MFA-SCOPE': {
        ...pick('Does that protection cover everyone with remote access: staff, contractors, suppliers and admins?', { all: 'Yes, everyone', some: 'Some of them', none: 'No' }), // prettier-ignore
        ...when('P-REMOTE'),
    },
    'UW-RDP': pick('How is Remote Desktop (RDP) used?', { not_used: 'Not used at all', internal_only: 'Only inside the office network', remote_access: 'For remote access', other: 'In another way' }), // prettier-ignore
    'UW-RDP-PROTECTIONS': {
        ...many('What protects Remote Desktop?', { vpn: 'VPN', mfa: 'Multi-factor authentication', nla: 'Network Level Authentication', honeypots: 'RDP honeypots', none: 'None of these' }), // prettier-ignore
        ...when('UW-RDP', 'not_used', 'ne'),
    },
    // ---------- accounts ----------
    'UW-ADMIN-USERS': count('How many people have administrator access in total?'),
    'UW-DOMAIN-ADMINS': count('How many people have full control of your directory, such as Active Directory domain admins?'), // prettier-ignore
    'UW-WIDE-ADMINS': count('How many people have standing admin rights on computers and servers other than their own?'), // prettier-ignore
    'UW-SERVICE-ACCOUNTS': count('How many service accounts are there?', 'Accounts that systems, not people, sign in with.'), // prettier-ignore
    'UW-ADMIN-JUSTIFICATION': { ...text('Why does the business need that many admin accounts, and is there a plan to reduce them?', '', true), mandatory: false }, // prettier-ignore
    'UW-BREAKGLASS-NO-MFA': count('How many emergency admin accounts sign in without MFA?'),
    'UW-PRIV-CONTROLS': many('Which of these are in place for admin accounts?', {
        local_admin_disabled: 'Built-in local admin accounts disabled',
        paw: 'Admin work done only from dedicated workstations without email or web access',
        pam: 'A privileged access management (PAM) tool',
        tiering: 'Admin access separated in tiers (like Microsoft’s tier model)',
        auto_logoff: 'Admin sessions watched and signed out when idle',
        directory_monitored: 'The directory watched in real time for unusual activity',
        account_audit_tool: 'A tool that audits and monitors service accounts',
        service_accounts_unprivileged: 'Service accounts kept out of admin groups',
        none: 'None of these',
    }),
    'UW-MFA-INDEPENDENT': pick('If one device is lost or hacked, does that give away only one of a person’s sign-in factors?', { yes: 'Yes', no: 'No', no_mfa: 'We don’t use MFA' }, 'For example, the password isn’t stored on the phone that receives the code.'), // prettier-ignore
    // ---------- email, backup and continuity ----------
    'UW-EMAIL-EXTRAS': many('Which of these does your email security do?', { quarantine: 'Suspicious email quarantined automatically', sandboxing: 'Attachments opened in a sandbox first', secure_send: 'Sensitive email to outsiders sent securely', none: 'None of these' }), // prettier-ignore
    'UW-BACKUP-PROTECTIONS': many('How are backups protected against ransomware?', {
        immutable: 'Immutable (write once, read many)',
        offline: 'Fully offline or air-gapped (tape or unplugged disks)',
        cloud_segmented: 'In the cloud, kept apart from your network',
        malware_scanned: 'Scanned for malware before they are backed up',
        none: 'None of these',
    }),
    'UW-CRITICAL-SYSTEMS': {
        type: 'table',
        text: 'Which systems, applications or services does the business depend on most?',
        help: 'In-house or outsourced: cloud apps, hosting, data processing and the like.',
        settings: {
            min_rows: 1,
            max_rows: 30,
            columns: [
                { key: 'system', label: 'System, application or service', type: 'text', required: true },
                { key: 'provider', label: 'Provider (blank if in-house)', type: 'text' },
                { key: 'bia', label: 'Business impact analysis done', type: 'boolean' },
                { key: 'rto_hours', label: 'Recovery time objective (hours)', type: 'number' },
                { key: 'rpo_hours', label: 'Recovery point objective (hours)', type: 'number' },
                { key: 'backup_frequency', label: 'How often it is backed up', type: 'text' },
            ],
        },
    },
    'UW-FAILOVER': pick('Do you have alternative systems ready for critical applications?', { yes: 'Yes', partly: 'For some', no: 'No' }), // prettier-ignore
    'UW-FAILOVER-KINDS': {
        ...many('What kind?', { active_active: 'Automatic failover (active–active)', active_passive: 'Automatic failover (active–passive)', manual: 'Manual failover', offline_environment: 'An offline alternative environment', alternative_provider: 'An alternative provider', colocation: 'A colocation facility' }), // prettier-ignore
        ...when('UW-FAILOVER', 'no', 'ne'),
    },
    'UW-RESILIENCE': many('Which of these does the business have?', { alternate_power: 'Alternative power for critical equipment', extra_bandwidth: 'Extra bandwidth available from another supplier', generators_tested: 'Generators or dual power supplies, tested', none: 'None of these' }), // prettier-ignore
    'UW-SECURE-DEV': pick('Are your software developers trained to write secure code?', { yes: 'Yes', no: 'No', no_developers: 'We have no developers' }), // prettier-ignore
    // ---------- monitoring ----------
    'UW-DDOS': bool('Do you have protection and a plan for denial-of-service attacks?'),
    'UW-MONITORING': many('Which of these do you use to prevent, spot and respond to attacks?', {
        antimalware: 'Next-generation antivirus with heuristic analysis',
        ids: 'Intrusion detection',
        ips: 'Intrusion prevention',
        web_filtering: 'Web or URL filtering',
        isolation: 'Application isolation and containment',
        advanced_endpoint: 'Advanced endpoint protection',
        mdr: 'Managed detection and response (MDR)',
        xdr: 'Extended detection and response (XDR)',
        manual_log_review: 'Manual log reviews',
        siem: 'A SIEM',
        soc: 'A security operations centre',
        soar: 'SOAR automation',
        managed_firewall: 'A managed firewall service',
        threat_intel: 'Threat intelligence feeds or services',
        none: 'None of these',
    }),
    'UW-EDR-PCT': pct('What share of computers and servers have EDR, MDR or XDR?'),
    'UW-EDR-PROVIDER': {
        ...text('Which EDR, MDR or XDR product or provider?'),
        ...when('UW-EDR-PCT', 0, 'gt'),
    },
    'UW-EDR-AUTO': { ...pick('Does it isolate or block suspicious activity automatically?', { yes: 'Yes', partly: 'Partly', no: 'No' }), ...when('UW-EDR-PCT', 0, 'gt') }, // prettier-ignore
    'UW-EDR-TO-SIEM': { ...pick('Do its alerts feed a SIEM, SOAR or central console?', { yes: 'Yes', partly: 'Partly', no: 'No' }), ...when('UW-EDR-PCT', 0, 'gt') }, // prettier-ignore
    'UW-SIEM-PCT': { ...pct('What share of critical logs feed the SIEM?'), visibility: { question: 'UW-MONITORING', op: 'contains', value: 'siem' }, depends_on: ['UW-MONITORING'] }, // prettier-ignore
    'UW-SIEM-PROVIDER': { ...text('Which SIEM?'), visibility: { question: 'UW-MONITORING', op: 'contains', value: 'siem' }, depends_on: ['UW-MONITORING'] }, // prettier-ignore
    'UW-SOC': { ...pick('Who runs the security operations centre?', { internal: 'Our own team', external: 'An outside provider', hybrid: 'Both' }), visibility: { question: 'UW-MONITORING', op: 'contains', value: 'soc' }, depends_on: ['UW-MONITORING'] }, // prettier-ignore
    'UW-SOC-247': { ...bool('Does it run around the clock?'), visibility: { question: 'UW-MONITORING', op: 'contains', value: 'soc' }, depends_on: ['UW-MONITORING'] }, // prettier-ignore
    // ---------- assets and patching ----------
    'UW-INVENTORY-PCT': pct('What share of your hardware and software is in your asset inventory?'),
    'UW-SCAN-SCOPE-PCT': pct('What share of your assets is covered by vulnerability scanning?'),
    'UW-CMDB': pick('Do you use a configuration management database (CMDB)?', { yes: 'Yes', partly: 'Partly', no: 'No' }), // prettier-ignore
    'UW-SCAN-INTERNAL': pick('How often do you scan internal systems for vulnerabilities?', SCAN_CADENCE),
    'UW-SCAN-EXTERNAL': pick('How often do you scan internet-facing systems for vulnerabilities?', SCAN_CADENCE),
    'UW-SCAN-TOOLS': { ...text('Which vulnerability scanning tools do you use?'), mandatory: false },
    'UW-EOL': bool('Do you run any hardware, software or systems the vendor no longer supports?'),
    'UW-EOL-FACTS': {
        ...many('Which of these are true of those systems?', { business_critical: 'Some are business-critical', sensitive_data: 'Some hold personal or confidential data', internet_restricted: 'They can’t reach the internet', segregated: 'They are isolated from the rest of the network' }), // prettier-ignore
        ...when('UW-EOL'),
    },
    'UW-EOL-DETAIL': { ...text('Which unsupported systems, what for, how many, and when will they be replaced?', '', true), ...when('UW-EOL') }, // prettier-ignore
    'UW-PATCH-DAYS-CRITICAL': { type: 'numeric', text: 'Within how many days do you aim to fix critical vulnerabilities?', settings: { integer: true, min: 0, unit: 'days' } }, // prettier-ignore
    'UW-PATCH-DAYS-HIGH': { type: 'numeric', text: 'Within how many days for high ones?', settings: { integer: true, min: 0, unit: 'days' } }, // prettier-ignore
    'UW-PATCH-DAYS-MEDIUM': { type: 'numeric', text: 'Within how many days for medium ones?', settings: { integer: true, min: 0, unit: 'days' } }, // prettier-ignore
    'UW-PATCH-DAYS-LOW': { type: 'numeric', text: 'Within how many days for low ones?', settings: { integer: true, min: 0, unit: 'days' } }, // prettier-ignore
    'UW-PATCH-TESTED': bool('Are patches tested before they are rolled out widely?'),
    // ---------- suppliers and cloud ----------
    'UW-VENDOR-TEAM': bool('Does someone in the business manage technology suppliers as part of their job?'),
    'UW-VENDOR-CHECKS': many('What do you check when you choose or review a technology supplier?', {
        infosec_cert: 'Security certifications',
        resilience_cert: 'Business resilience certifications',
        pentest: 'Penetration test results',
        rating_service: 'A security rating service',
        backup_review: 'Their backup procedures',
        sla: 'Service levels',
        mfa_review: 'Their use of MFA',
        dpia: 'A data protection impact assessment',
        dpa: 'Data protection terms in the contract',
        none: 'None of these',
    }),
    'UW-CLOUD-POLICY': bool('Do you have a written cloud security policy?'),
    'UW-CLOUD-CONTROLS': many('Which of these protect your cloud services?', { casb: 'A cloud access security broker (CASB)', sase: 'Secure access service edge (SASE)', ztna: 'Zero trust network access', mfa_critical: 'MFA on business-critical cloud apps', mfa_other: 'MFA on other cloud apps', none: 'None of these' }), // prettier-ignore
    // ---------- acquisitions, media, losses ----------
    'UW-ACQUISITIONS': count('How many businesses have you acquired in the last three years?'),
    'UW-ACQUISITION-DETAIL': { ...text('Which ones, how large, and when?', '', true), ...when('UW-ACQUISITIONS', 0, 'gt') }, // prettier-ignore
    'UW-ACQUISITION-REVIEW': { ...pick('When do you check an acquired business’s cyber security?', { before: 'Before buying it', before_integration: 'After buying it, before joining its systems to ours', rarely: 'Rarely, before or after' }), ...when('UW-ACQUISITIONS', 0, 'gt') }, // prettier-ignore
    'UW-MEDIA': many('Which of these are true of what you publish (website, social media, advertising)?', {
        trademarks_screened: 'A lawyer has checked our trademarks, domain names and metatags',
        releases_obtained: 'We get written permission from outside content creators',
        legal_review: 'A lawyer reviews content before it is published or after a complaint',
        outsourced_content: 'Agencies or other outsiders create or manage content for us',
        indemnities: 'Those outsiders indemnify us',
        policies_reviewed: 'A lawyer has reviewed our privacy policy and terms',
        none: 'None of these',
    }),
    'UW-LOSSES': many('Which of these has the business had in the last five years?', {
        data_breach: 'A data breach',
        cyber_incident: 'A malicious cyber incident, such as ransomware or hacking',
        system_failure: 'A system outage or failure',
        media_claim: 'A claim over something it published',
        regulatory_action: 'A regulator’s action over data or system security',
        supplier_breach: 'A data breach at a supplier',
        supplier_incident: 'A cyber incident at a supplier that affected us',
        none: 'None of these',
    }, 'Leave out events your security stopped.'), // prettier-ignore
    'UW-LOSS-DETAIL': {
        type: 'repeating_group',
        text: 'Tell us about each one.',
        visibility: { not: { question: 'UW-LOSSES', op: 'contains', value: 'none' } },
        depends_on: ['UW-LOSSES'],
        settings: {
            item_label: 'Event',
            fields: [
                { key: 'what', label: 'What happened, and when', type: 'text', required: true },
                { key: 'impact', label: 'What it cost the business', type: 'text' },
                { key: 'mitigation', label: 'What you changed afterwards', type: 'text' },
            ],
        },
    },
    'UW-CIRCUMSTANCES': bool('Do you know of anything that could still lead to a claim: a breach, incident, outage, regulator’s action or complaint?'), // prettier-ignore
    'UW-CIRCUMSTANCES-DETAIL': { ...text('What is it?', '', true), ...when('UW-CIRCUMSTANCES') },
    // ---------- industry supplements ----------
    'UW-OT-POLICY': bool('Do you have a security policy for your production or control systems that covers cyber?'),
    'UW-OT-SITES': count('How many production sites do you run?'),
    'UW-EMV-PCT': pct('What share of your card terminals take chip (EMV) cards?'),
    'UW-PAYMENT-PROVIDERS': text('Which providers process your card payments?'),
    'UW-POS-MONITORED': bool('Are your point-of-sale systems protected by antimalware and watched by your security team or provider?'), // prettier-ignore
    'UW-FRANCHISE': bool('Do you have franchisees?'),
    'UW-FRANCHISE-DETAIL': { ...text('Who looks after cyber security at the franchisees, and how do you make sure it is done the same way?', '', true), ...when('UW-FRANCHISE') }, // prettier-ignore
    'UW-PI-INSURANCE': bool('Does the business have professional indemnity insurance?'),
    'UW-PI-CYBER-EXCLUSION': { ...bool('Does that policy exclude cyber events?'), ...when('UW-PI-INSURANCE') },
    // ---------- cover ----------
    'UW-CURRENT-COVER': {
        type: 'table',
        text: 'Which of these policies do you have now?',
        mandatory: false,
        settings: {
            columns: [
                { key: 'cover', label: 'Cover', type: 'choice', options: ['cyber', 'crime', 'professional_indemnity'], required: true }, // prettier-ignore
                { key: 'limit', label: 'Limit (RM)', type: 'number' },
                { key: 'excess', label: 'Excess (RM)', type: 'number' },
                { key: 'premium', label: 'Premium (RM)', type: 'number' },
                { key: 'insurer', label: 'Insurer', type: 'text' },
                { key: 'expiry', label: 'Expiry date', type: 'date' },
            ],
        },
    },
    'UW-LIMIT-WANTED': money('How much cyber cover would you like quoted?', 'The limit of indemnity, for example RM1 million.'), // prettier-ignore
};
/**
 * Where the facts are asked (task 89): three stages after "Insurance readiness", entered once the
 * assessment prepares an insurer's form. Each fact is shown only when a chosen form needs it.
 */
export const UW_STAGES = {
    S10: [
        'UW-REG-NO', 'UW-TAX-NO', 'UW-SST-NO', 'UW-ADDRESS', 'UW-YEAR-EST', 'UW-SEC-CONTACT', 'UW-ACTIVITIES',
        'UW-SPECIAL-ACTIVITIES', 'UW-PARENT', 'UW-PARENT-DETAIL', 'UW-SUBSIDIARIES', 'UW-GROUP-SECURITY',
        'UW-SANCTIONS', 'UW-OVERSEAS-ENTITIES', 'UW-OVERSEAS-LIST', 'UW-REVENUE-CURRENT', 'UW-REVENUE-NEXT',
        'UW-REVENUE-OVERSEAS-PCT', 'UW-REVENUE-US-PCT', 'UW-ONLINE-PCT',
        'UW-ACQUISITIONS', 'UW-ACQUISITION-DETAIL', 'UW-ACQUISITION-REVIEW', 'UW-MEDIA', 'UW-PI-INSURANCE',
        'UW-PI-CYBER-EXCLUSION', 'UW-LOSSES', 'UW-LOSS-DETAIL', 'UW-CIRCUMSTANCES', 'UW-CIRCUMSTANCES-DETAIL',
        'UW-CURRENT-COVER', 'UW-LIMIT-WANTED',
    ],
    S11: [
        'UW-PII-RECORDS', 'UW-CARD-RECORDS', 'UW-HEALTH-RECORDS', 'UW-PROCESSOR', 'UW-PROCESSOR-DETAIL', 'UW-PCI',
        'UW-PCI-TXNS', 'UW-PCI-OUTSOURCED', 'UW-PCI-LEVEL', 'UW-EMV-PCT', 'UW-PAYMENT-PROVIDERS', 'UW-POS-MONITORED',
        'UW-FRANCHISE', 'UW-FRANCHISE-DETAIL',
        'UW-PDPA-REG', 'UW-PDPA-REG-DATE', 'UW-COMPLIANCE-REVIEW', 'UW-STANDARDS', 'UW-PII-PROTECTIONS',
        'UW-PII-ENCRYPTED-PCT', 'UW-PII-LARGEST-STORE', 'UW-PII-PROCESSORS', 'UW-PROCESSOR-CONTRACTS',
        'UW-RECOURSE-WAIVED', 'UW-BIOMETRIC', 'UW-BIOMETRIC-FROM', 'UW-BIOMETRIC-CONSENT', 'UW-BIOMETRIC-POLICY',
        'UW-BIOMETRIC-RECORDS', 'UW-HOSTED-CENTRALLY', 'UW-SEGMENTATION', 'UW-MOBILE-ACCESS', 'UW-MDM-COMPANY-PCT',
        'UW-MDM-BYOD-PCT', 'UW-REMOTE-METHODS', 'UW-REMOTE-MFA-SCOPE', 'UW-RDP', 'UW-RDP-PROTECTIONS',
        'UW-ADMIN-USERS', 'UW-DOMAIN-ADMINS', 'UW-WIDE-ADMINS', 'UW-SERVICE-ACCOUNTS', 'UW-ADMIN-JUSTIFICATION',
        'UW-BREAKGLASS-NO-MFA', 'UW-PRIV-CONTROLS', 'UW-MFA-INDEPENDENT', 'UW-CLOUD-POLICY', 'UW-CLOUD-CONTROLS',
    ],
    S12: [
        'UW-EMAIL-EXTRAS', 'UW-BACKUP-PROTECTIONS', 'UW-CRITICAL-SYSTEMS', 'UW-FAILOVER', 'UW-FAILOVER-KINDS',
        'UW-RESILIENCE', 'UW-SECURE-DEV', 'UW-DDOS', 'UW-MONITORING', 'UW-EDR-PCT', 'UW-EDR-PROVIDER', 'UW-EDR-AUTO',
        'UW-EDR-TO-SIEM', 'UW-SIEM-PCT', 'UW-SIEM-PROVIDER', 'UW-SOC', 'UW-SOC-247', 'UW-PENTEST', 'UW-INVENTORY-PCT',
        'UW-SCAN-SCOPE-PCT', 'UW-CMDB', 'UW-SCAN-INTERNAL', 'UW-SCAN-EXTERNAL', 'UW-SCAN-TOOLS', 'UW-EOL',
        'UW-EOL-FACTS', 'UW-EOL-DETAIL', 'UW-PATCH-DAYS-CRITICAL', 'UW-PATCH-DAYS-HIGH', 'UW-PATCH-DAYS-MEDIUM',
        'UW-PATCH-DAYS-LOW', 'UW-PATCH-TESTED', 'UW-VENDOR-TEAM', 'UW-VENDOR-CHECKS', 'UW-OT-POLICY', 'UW-OT-SITES',
    ],
}; // prettier-ignore
/**
 * What a group's IT environments share (task 94): the forms chosen and the facts about the company as a
 * whole (S10). An environment's own security answers are its own: that is why Chubb asks for a form each.
 */
export const GROUP_FACTS = ['UW-FORMS', ...UW_STAGES.S10];
//# sourceMappingURL=underwriting-facts.js.map